import { useState, useEffect } from "react";
import api from "../../../../../Axios";
import { toast } from "react-toastify";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  InputLabel
} from "@mui/material";
import { Button as MuiButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function Step2Schedule({
  programId,
  initialData,
  setMasterRender,
  next,
  prev
}) {

  const [loading, setLoading] = useState(false);

  const [certificateFile, setCertificateFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const [certificatePreview, setCertificatePreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    duration_hours: 0,
    estimated_cost: 0,
    is_certified: false,
    certificate_sample_img: "",
    program_banner_image: "",
    documentPath: ""
  });

  // Upload file
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await api.post(
      "/lnd/upload-program-files",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return res.data?.data?.fileName;
  };

  // Load initial data
  useEffect(() => {
    if (initialData) {
      setForm({
        duration_hours: Number(initialData.duration_hours) || 0,
        estimated_cost: Number(initialData.estimated_cost) || 0,
        is_certified: Boolean(initialData.is_certified),
        certificate_sample_img:
          initialData.certificate_sample_img || "",
        program_banner_image:
          initialData.program_banner_image || "",
        documentPath: initialData.documentPath || ""
      });
    }
  }, [initialData]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (certificatePreview)
        URL.revokeObjectURL(certificatePreview);
      if (bannerPreview)
        URL.revokeObjectURL(bannerPreview);
    };
  }, [certificatePreview, bannerPreview]);

  const validateForm = () => {
    let newErrors = {};
    if (!form.duration_hours || form.duration_hours <= 0) {
      newErrors.duration_hours = "Duration in hours is required and must be greater than 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const save = async (isNext) => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      let certificateName = form.certificate_sample_img;
      let bannerName = form.program_banner_image;

      if (certificateFile) {
        certificateName = await uploadFile(certificateFile);
      }

      if (bannerFile) {
        bannerName = await uploadFile(bannerFile);
      }

      await api.put(`/lnd/${programId}/schedule`, {
        ...form,
        certificate_sample_img: certificateName,
        program_banner_image: bannerName
      });

      toast.success(isNext ? "Your changes have been saved. Continue updating the details" : "Schedule data saved successfully");

      setMasterRender((prev) => !prev);

      if (isNext) next();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        "Failed to save schedule"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>

      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h5 className="mb-4 fw-bold">Schedule & Media</h5>

        {/* Duration */}
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Duration (Hours) *"
          placeholder="Enter duration in hours"
          value={form.duration_hours}
          inputProps={{ min: 0 }}
          onChange={(e) => {
            setForm({
              ...form,
              duration_hours: e.target.value
            });
            if (errors.duration_hours) setErrors({ ...errors, duration_hours: "" });
          }}
          error={!!errors.duration_hours}
          helperText={errors.duration_hours}
          sx={{ mb: 3 }}
        />

        {/* Estimated Cost */}
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Estimated Cost(₹)"
          placeholder="Enter estimated cost"
          value={form.estimated_cost}
          inputProps={{ min: 0 }}
          onChange={(e) =>
            setForm({
              ...form,
              estimated_cost: e.target.value
            })
          }
          sx={{ mb: 3 }}
        />

        {/* Certification */}
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Checkbox
              checked={form.is_certified}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_certified: e.target.checked
                })
              }
            />
          }
          label="Certification Available"
        />

        {/* Certificate Image */}
        {form.is_certified && (
          <Box sx={{ mb: 3 }}>

            <div>
              <InputLabel>
                Upload Certificate Sample Image (JPEG, JPG, PNG, max 4 MB)
              </InputLabel>
              <MuiButton
                component="label"
                variant="contained"
                color="success"
                size="small"
                startIcon={<CloudUploadIcon />}
              // disabled={isSubmitting}
              >
                Upload files
                <VisuallyHiddenInput
                  type="file"
                  multiple
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const maxSize = 4 * 1024 * 1024;
                    const allowedTypes = ["image/jpeg", "image/png"];

                    if (!allowedTypes.includes(file.type)) {
                      toast.error("Only JPEG, JPG and PNG files are allowed");
                      e.target.value = null;
                      return;
                    }

                    if (file.size > maxSize) {
                      toast.error("File size should not exceed 4MB")
                      e.target.value = null;
                      return;
                    }

                    setCertificateFile(file);
                    setCertificatePreview(URL.createObjectURL(file));
                  }}
                  accept=".jpg,.jpeg,.png"
                />
              </MuiButton>
            </div>

            {(certificatePreview || form.certificate_sample_img) && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={
                    certificatePreview
                      ? certificatePreview
                      : BASE_URL + form.documentPath + form.certificate_sample_img
                  }
                  alt="Certificate Preview"
                  style={{
                    maxHeight: "150px",
                    borderRadius: "8px",
                    border: "1px solid #ddd"
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Banner Image */}
        <Box sx={{ mb: 3 }}>

          <div>
            <InputLabel>
              Upload Banner Image (JPEG, JPG, PNG, max 4 MB)
            </InputLabel>
            <MuiButton
              component="label"
              variant="contained"
              color="success"
              size="small"
              startIcon={<CloudUploadIcon />}
            // disabled={isSubmitting}
            >
              Upload files
              <VisuallyHiddenInput
                type="file"
                multiple
                onChange={(e) => {
                  const file = e.target.files[0];

                  if (!file) return;

                  const maxSize = 4 * 1024 * 1024;
                  const allowedTypes = ["image/jpeg", "image/png"];

                  if (!allowedTypes.includes(file.type)) {
                    toast.error("Only JPG and PNG files are allowed");
                    e.target.value = null;
                    return;
                  }

                  if (file.size > maxSize) {
                    toast.error("File size should not exceed 4MB")
                    e.target.value = null;
                    return;
                  }

                  setBannerFile(file);

                  if (file) {
                    setBannerPreview(
                      URL.createObjectURL(file)
                    );
                  }
                }}
                accept=".jpg,.jpeg,.png"
              />
            </MuiButton>
          </div>

          {(bannerPreview || form.program_banner_image) && (
            <Box sx={{ mt: 2 }}>
              <img
                src={
                  bannerPreview
                    ? bannerPreview
                    : form.documentPath + form.program_banner_image
                }
                alt="Banner Preview"
                style={{
                  maxHeight: "150px",
                  borderRadius: "8px",
                  border: "1px solid #ddd"
                }}
              />
            </Box>
          )}
        </Box>
      </div>

      {/* Buttons */}
      <div className="d-flex justify-content-between mt-4 px-2">
        <button
          className="btn btn-secondary px-4 fw-medium"
          onClick={prev}
          disabled={loading}
        >
          Previous
        </button>

        <div className="d-flex gap-3">
          <button
            className="btn btn-secondary px-4 fw-medium"
            onClick={() => save(0)}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save as Draft"}
          </button>

          <button
            className="btn btn-success px-4 fw-medium"
            onClick={() => save(1)}
            disabled={loading}
          >
            Save & Continue
          </button>
        </div>
      </div>

    </div>
  );
}